/**
 * deathTrashTalk.ts — Burnt Peanut roast lines for the death screen
 * =====================================================================
 * BETA 15.93 — From PSBOT RUN handoff (2026-05-05-death-screen-rip-peanut-spec.md).
 * Per Dylan: "use what ps bot gave you. to test."
 *
 * 50+ trash-talk lines with optional filters. selectTrashLines() picks
 * a deterministic-ish subset for a given death so the same line set
 * shows for the duration of one death sequence (no mid-sequence pool
 * shuffles), then a different mix on the next death.
 * =====================================================================
 */

import type { Grade } from './deathGrade';

export interface TrashLine {
  text: string;
  filter?: {
    minSurvival?: number;     // only fire if survived > X seconds
    maxSurvival?: number;     // only fire if survived < X seconds
    minKills?: number;
    maxKills?: number;
    character?: string[];     // only for these character ids
    deathCause?: string[];    // matches against the bare enemy/boss type, e.g. 'zombie' / 'timmy'
    grade?: Grade[];          // only fire when computed grade is in this set
  };
}

export interface TrashTalkInputs {
  survivalSeconds: number;
  killCount: number;
  character: string;     // character id, e.g. 'cowboy', 'wizard'
  deathCause: string;    // bare type, e.g. 'zombie', or 'won' if victorious
  grade: Grade;
}

export const TRASH_TALK_POOL: TrashLine[] = [
  // ── Generic Burnt Peanut energy (no filter) ─────────────────────
  { text: "Imagine dying. Couldn't be the peanut." },
  { text: "That run was so bad your save file is in therapy." },
  { text: "Bro you really thought you were him. You weren't." },
  { text: "The peanut watched the whole thing. The peanut is disappointed." },
  { text: "Skill issue ain't even the right word. It's a YOU issue." },
  { text: "Two million Bungulators just saw you fold like a lawn chair." },
  { text: "You played that run like the controller was upside down." },
  { text: "Was that a build or did you just close your eyes and click?" },
  { text: "The peanut would've cleared this with a bag of chips." },
  { text: "Imagine being so bad the goop felt embarrassed for you." },
  { text: "Your DPS was so low the enemies took a smoke break." },
  { text: "The lobby's quiet. Everyone's processing how bad that was." },
  { text: "Bro the death screen stayed on for two minutes just to disrespect you." },
  { text: "That run had so many mistakes the peanut lost count, and the peanut counts fast." },
  { text: "The peanut just took a sip of water for you. That's the only respect you're getting." },
  { text: "Welp. That happened. The peanut signs off." },
  { text: "Get up. Try again. Or don't. The peanut does not care." },
  { text: "Even your character is mad at you. Look at his face. He KNOWS." },
  { text: "The peanut used to believe in you. Used to." },
  { text: "You died like you live: confused and underprepared." },
  { text: "Mhm. Mhm. Sorry boss. That was rough." },
  { text: "Brother that wasn't a build, that was a cry for help." },
  { text: "The peanut would've sent that to coaching but coaching said no." },
  { text: "I've seen toddlers play this game with more discipline." },
  { text: "The peanut scrolled past your highlight reel without watching." },
  { text: "Some die. Some THRIVE. You? You did neither." },
  { text: "Imagine paying for chests just to die in a hallway. Couldn't be me." },
  { text: "Peanut nation collectively winced. The peanut nation has spoken." },
  { text: "Take no prisoners — except yourself, apparently." },
  { text: "Hawk tuah on your build, peanut out." },
  { text: "Well ahh. That was a choice." },
  { text: "Bungulators are crying. Not because you died. Because you SUCKED." },
  { text: "Logging this in the cringe compilation. Position 1." },
  { text: "Peanut needs a moment. The peanut is going to go play Rust." },
  { text: "If the peanut had a dollar for every bad decision you made, the peanut would have a server." },

  // ── Low survival (< 60s) — quick deaths ─────────────────────────
  // BETA 16.05 — Per Dylan: "said twelve kills peanut do better or
  // something like that when in stats it says i had 16 kills. shit talk
  // is not matching." Replaced hardcoded numbers with {kills}/{seconds}
  // templates that get substituted at render time with the run's actual
  // values. Now the line ALWAYS reads true.
  { text: "{seconds} seconds? The peanut takes longer to roast.", filter: { maxSurvival: 60 } },
  { text: "Speed-run to death. Impressive in the wrong direction.", filter: { maxSurvival: 60 } },
  { text: "You died faster than the peanut can say 'sorry boss.'", filter: { maxSurvival: 60 } },
  { text: "Bro you logged on just to log off.", filter: { maxSurvival: 45 } },

  // ── Long survival (> 200s) — late-run deaths ────────────────────
  { text: "Three minutes deep and you STILL ate it? The grind was real, the skill was not.", filter: { minSurvival: 200 } },
  { text: "Long run, dumb death. Classic.", filter: { minSurvival: 200 } },
  { text: "All that progress just to feed the goop. Tragic.", filter: { minSurvival: 200 } },

  // ── Low kills (< 50) ────────────────────────────────────────────
  { text: "{kills} kills? The peanut sneezes harder than that.", filter: { maxKills: 20 } },
  { text: "You spent more time RUNNING than killing. Coward energy.", filter: { maxKills: 50 } },
  { text: "The enemies died of old age before you could shoot them.", filter: { maxKills: 30 } },

  // ── High kills + early death ────────────────────────────────────
  { text: "Big numbers. Quick death. You're the streamer who clips themselves losing.", filter: { minKills: 200, maxSurvival: 120 } },
  { text: "Peaked at kill count, valley'd at staying alive. Pick a lane.", filter: { minKills: 200, maxSurvival: 120 } },

  // ── Character-specific ──────────────────────────────────────────
  { text: "Chad died with the AURA on. Aura don't save bad gameplay.", filter: { character: ['chad'] } },
  { text: "Killa? More like NOT-A-killa. That run was peaceful.", filter: { character: ['killa'] } },
  { text: "Sponge soaked up DEATH like he soaked up XP. Both badly.", filter: { character: ['sponge'] } },
  { text: "Viking's village is canceling the funeral. Embarrassing.", filter: { character: ['viking'] } },
  { text: "Cowboy said yeehaw and then said yee-OW.", filter: { character: ['cowboy'] } },
  { text: "Mr. Silly lived up to the name. Died silly too.", filter: { character: ['mr_silly'] } },
  { text: "Pirate found the treasure: it was DEATH.", filter: { character: ['pirate'] } },
  { text: "Tactical was anything but. Strategic failure.", filter: { character: ['tactical'] } },
  { text: "Wizard couldn't conjure a healthbar.", filter: { character: ['wizard'] } },
  { text: "Lebron missed the layup. The kingdom is silent.", filter: { character: ['lebron'] } },

  // ── Death-cause specific ────────────────────────────────────────
  { text: "Killed by a ZOMBIE. The starter enemy. The peanut is sending a card.", filter: { deathCause: ['zombie'] } },

  // ── Grade-specific (BETA 15.93) ─────────────────────────────────
  // S = peanut-coded. Backhanded compliments only.
  { text: "Okay. Okay. The peanut sees you. Briefly.", filter: { grade: ['S'] } },
  { text: "That was actually impressive. The peanut hates that for you.", filter: { grade: ['S'] } },
  // F = the absolute trash tier. Pure roast.
  { text: "F? That's generous. The peanut would've given you an F-minus-minus.", filter: { grade: ['F'] } },
  { text: "Absolute trash. The peanut framed your stat sheet as a warning to others.", filter: { grade: ['F'] } },
  { text: "F as in FORFEIT. Just stop.", filter: { grade: ['F'] } },
];

/**
 * Light shuffle — picks `count` lines from the eligible pool. Uses a
 * simple hash of the survival time as a seed so the same death gets a
 * stable mix (no flicker between renders). Different death = different
 * mix.
 */
function seededShuffle<T>(arr: T[], seed: number): T[] {
  const out = arr.slice();
  let s = seed | 0;
  for (let i = out.length - 1; i > 0; i--) {
    // xorshift-ish PRNG
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    const j = Math.abs(s) % (i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/**
 * Pick the trash-talk lines that will display during ONE death sequence.
 * Filters by run stats first, then random-shuffles down to `count`.
 * Always returns at least one line — generic pool guarantees fallback.
 */
export function selectTrashLines(inputs: TrashTalkInputs, count: number = 6): TrashLine[] {
  const eligible = TRASH_TALK_POOL.filter(line => {
    if (!line.filter) return true;
    const f = line.filter;
    if (f.minSurvival !== undefined && inputs.survivalSeconds < f.minSurvival) return false;
    if (f.maxSurvival !== undefined && inputs.survivalSeconds > f.maxSurvival) return false;
    if (f.minKills !== undefined && inputs.killCount < f.minKills) return false;
    if (f.maxKills !== undefined && inputs.killCount > f.maxKills) return false;
    if (f.character && !f.character.includes(inputs.character)) return false;
    if (f.deathCause && !f.deathCause.includes(inputs.deathCause)) return false;
    if (f.grade && !f.grade.includes(inputs.grade)) return false;
    return true;
  });
  // Seed the shuffle with a hash of inputs so the same death produces
  // the same line set every render frame (no flicker).
  const seed = Math.round(inputs.survivalSeconds * 1000) ^ inputs.killCount ^ inputs.character.charCodeAt(0);
  const shuffled = seededShuffle(eligible, seed || 1);
  return shuffled.slice(0, count);
}
